import { Field, Form, Formik, FormikProps } from "formik";
import {
  Button,
  Input,
  Textarea,
  Checkbox,
  SelectComponent,
  CalendarInput,
  DropFileInput,
} from "shared";
import {
  getFormConfig,
  getValidationSchema,
  getInitialValues,
  InvestmentFormValues,
  InvestmentFieldConfig,
} from "./forms";
import RadioGroup from "./fields/RadioGroup";
import ComputedField from "./fields/ComputedField";
import { useGetCountriesCurrency } from "hooks/countries";
import "./InvestmentForm.scss";

interface InvestmentFormProps {
  slug: string;
  onSubmit: (values: InvestmentFormValues) => void;
  onCancel: () => void;
  initialData?: Partial<InvestmentFormValues>;
  isLoading?: boolean;
  submitLabel?: string;
}

const renderField = (
  field: InvestmentFieldConfig,
  formik: FormikProps<InvestmentFormValues>,
  currencyOptions?: any[],
) => {
  const { name, label, type, placeholder } = field;

  if (
    field.showWhen &&
    String(formik.values[field.showWhen.field] || "") !== field.showWhen.value
  ) {
    return null;
  }

  const error =
    (formik.touched[name] || formik.submitCount > 0) && formik.errors[name]
      ? String(formik.errors[name])
      : undefined;

  switch (type) {
    case "text":
    case "number":
      return (
        <div className="investment-form__field" key={name}>
          <Field
            as={Input}
            name={name}
            label={label}
            type={type}
            placeholder={placeholder}
            step={field.step}
            precision={field.precision}
            inputMode={field.inputMode}
            maxLength={field.maxLength}
            optional={field.required === false}
            error={error}
            data-suffix={field.suffix}
          />
        </div>
      );
    case "textarea":
      return (
        <div className="investment-form__field" key={name}>
          <Field
            component={Textarea}
            name={name}
            label={label}
            placeholder={placeholder}
            optional={field.required === false}
            error={error}
          />
        </div>
      );
    case "select":
      return (
        <div className="investment-form__field" key={String(name)}>
          <Field
            as={SelectComponent}
            options={
              name === "currencyUuid" ? currencyOptions : field.options || []
            }
            name={name}
            label={label}
            isSearchable={true}
            optional={field.required === false}
          />
        </div>
      );
    case "checkbox":
      return (
        <div className="investment-form__field" key={String(name)}>
          <Field
            as={Checkbox}
            name={String(name)}
            label={field.consentText || label}
            checked={formik.values[name]}
            onChange={() =>
              formik.setFieldValue(String(name), !formik.values[name])
            }
          />
        </div>
      );
    case "radio":
      return (
        <div className="investment-form__field" key={String(name)}>
          <RadioGroup
            name={String(name)}
            label={label}
            options={field.options || []}
            value={String(formik.values[name] || "")}
            onChange={(val) => formik.setFieldValue(String(name), val)}
            error={error}
            hasOtherInput={field.hasOtherInput}
            otherFieldName={field.otherFieldName}
            otherValue={
              field.otherFieldName
                ? String(formik.values[field.otherFieldName] || "")
                : ""
            }
            onOtherChange={
              field.otherFieldName
                ? (val) => formik.setFieldValue(field.otherFieldName!, val)
                : undefined
            }
          />
        </div>
      );
    case "computed":
      return (
        <div className="investment-form__field" key={name}>
          <ComputedField
            label={label}
            value={field.computeFn ? field.computeFn(formik.values) : ""}
            inline={field.inline}
          />
        </div>
      );
    case "date":
      return (
        <div className="investment-form__field" key={String(name)}>
          <Field
            type="date"
            name={String(name)}
            label={label}
            optional={field.required === false}
            component={CalendarInput}
            errors={formik.errors}
          />
        </div>
      );
    case "file":
      return (
        <div className="investment-form__field" key={String(name)}>
          <Field
            label={label}
            name={String(name)}
            id={String(name)}
            as={DropFileInput}
            accept={field.accept ?? ".pdf,.doc,.docx,.jpg,.jpeg,.png"}
            optional
            multiple={field.multiple}
            description={
              field.multiple
                ? `Maksymalnie ${field.multiple} plików`
                : undefined
            }
          />
        </div>
      );
    default:
      return null;
  }
};

const InvestmentForm = ({
  slug,
  onSubmit,
  onCancel,
  initialData,
  isLoading = false,
  submitLabel = "Zapisz",
}: InvestmentFormProps) => {
  const config = getFormConfig(slug);
  const validationSchema = getValidationSchema(slug);
  const initialValues = getInitialValues(slug, initialData);
  const { data: currencyOptions } = useGetCountriesCurrency();

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        const transformed = config.transformValues
          ? config.transformValues(values)
          : values;
        onSubmit(transformed as InvestmentFormValues);
      }}
      enableReinitialize
    >
      {(formik) => {
        console.log(formik.errors);
        return (
          <Form className="investment-form">
            <div className="investment-form__fields">
              {config.fields.map((field) =>
                renderField(field, formik, currencyOptions),
              )}
            </div>

            <div className="investment-form__actions">
              <Button
                type="button"
                onClick={onCancel}
                label="Anuluj"
                className="button--stroke button--rounded"
                disabled={isLoading}
              />
              <Button
                type="submit"
                onClick={() => console.log()}
                label={submitLabel}
                className="button--rounded button--black"
                isLoading={isLoading}
              />
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default InvestmentForm;
